import React, { forwardRef } from 'react';
import { Box, BoxProps } from '@mui/material';
import { FileMissingIcon } from '@/src/components/icon/stroke/FileMissingIcon';

export interface VideoProps extends BoxProps<'video'> {
  /** The URL of the video to embed. */
  src?: string;
  /** video type property, @default "video/mp4" */
  type?: string;
  /** The message (or component) to display if the video cannot be loaded, @default <Icon name="file-missing" /> centered inside the Box  */
  noVidErrorMessage?: React.ReactNode;
  /** Custom source Box props. */
  sourceProps?: BoxProps<'source'>;
  /** Image to be shown while the video is downloading or until the user hits the play button. */
  poster?: string;
  /** Video element autoPlay, @default true */
  autoPlay?: boolean;
  /** Video element muted, @default true */
  muted?: boolean;
  /** Video element native browser controls, @default false */
  controls?: boolean;
  /** Video element loop, @default false */
  loop?: boolean;
}

/**
 *  The Video component is a customizable video player built with React and Material-UI.
 *  It extends the Box component from Material-UI and provides various props to control video playback and appearance.
 *  If children are provided, they are rendered inside the video element as source elements.
 *  If no children are provided, a default source element is used based on the src prop.
 *  @param videoRef - A reference to the video element.
 */
export const Video: React.FC<VideoProps> = forwardRef(
  (
    {
      src,
      type = 'video/mp4',
      noVidErrorMessage,
      sourceProps,
      poster,
      sx,
      autoPlay = true,
      muted = true,
      controls = false,
      loop = false,
      children,
      ...rest
    },
    ref,
  ) => {
    return (
      <Box
        component="video"
        data-testid="Video"
        poster={poster}
        ref={ref}
        autoPlay={autoPlay}
        muted={muted}
        controls={controls}
        loop={loop}
        sx={{
          display: 'block',
          maxWidth: '100%',
          ...sx,
        }}
        {...rest}
      >
        {children ?? <Box component="source" src={src} type={type} {...sourceProps} />}
        {noVidErrorMessage ?? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
            <FileMissingIcon />
          </Box>
        )}
      </Box>
    );
  },
);
